import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toast, useToast } from "@shared/hooks/use-toast";
import { api, toggleServiceConfiguration } from "./api";
import {
  ComplaintCategory,
  CreateComplaintConfiguration,
  CreateComplaintRequest,
  CreateServiceConfiguartion,
  CreateServiceRequest,
  UpdateComplaintConfiguration,
  updateServiceCategories,
  updateServiceConfig,
} from "./types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";

export const useServiceCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceCategory.list(),
    () => api.service.category.get(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useSearchServiceCategory = (search_data: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceCategory.search(search_data),
    () => api.service.category.search(remoteUtilityId, search_data),
    {
      enabled: !!remoteUtilityId && !!search_data,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetServiceSubCategory = (code_list?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceSubCategory.list(code_list),
    () => api.service.subCategory.getByCategory(remoteUtilityId, code_list),
    {
      enabled: !!code_list && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useServiceSubCategory = (code?: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceSubCategory.list(code),
    () => api.service.subCategory.get(remoteUtilityId, code),
    {
      enabled: !!code && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.utility.utilityList(),
    () => api.utility.getServices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useCreateServiceCategory = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => api.service.category.create(payload),
    {
      onSuccess: (_data, payload) => {
        toast({ title: "Service category created successfully!" });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useCreateServiceSubCategory = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.service.subCategory.create(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Service sub-category created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceSubCategory.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceSubCategory.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const updateStatus = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) =>
      api.service.category.updateStatus(remoteUtilityId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Service category status updated successfully!!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceSubCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.status(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};
export const updateComplaintStatus = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) =>
      api.service.category.updateStatus(remoteUtilityId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Complaint category status updated successfully!!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.status(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const updateServiceCategory = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: updateServiceCategories) =>
      api.service.subCategory.update(payload),
    {
      onSuccess: () => {
        toast({ title: "Service category updated successfully!" });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.update(),
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "serviceSubCategory", "list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "serviceCategory", "filter"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const deleteServiceCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (category: any) => api.service.category.delete(remoteUtilityId, category),
    {
      onSuccess: () => {
        toast({
          title: "Service category deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const deleteServiceSubCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (category: any) =>
      api.service.subCategory.delete(remoteUtilityId, category),
    {
      onSuccess: () => {
        toast({
          title: "Service subcategory deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceSubCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceCategory.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceSubCategory.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useAllComplaintCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintCategory.list(),
    () => api.complaint.category.getAll(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useSearchAllComplaintCategory = (search_data: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintCategory.search(search_data),
    () => api.complaint.category.search(remoteUtilityId, search_data),
    {
      enabled: !!remoteUtilityId && !!search_data,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateComplaintCategory = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(
    (data: any) => api.complaint.category.create(remoteUtilityId, data),
    {
      onSuccess: () => {
        toast({
          title: "Complaint category created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const deleteComplaintCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (category: any) => api.complaint.category.delete(remoteUtilityId, category),
    {
      onSuccess: () => {
        toast({
          title: "Complaint category deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const deleteComplaintSubCategory = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (category: any) =>
      api.complaint.subCategory.delete(remoteUtilityId, category),
    {
      onSuccess: () => {
        toast({
          title: "Complaint subcategory deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintSubCategory.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintSubCategory.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const updateComplaintCategory = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.service.subCategory.update(payload),
    {
      onSuccess: (updatedServiceCategory) => {
        toast({
          title: "Complaint category updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.search(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintCategory.update(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useCreateComplaintSubCategory = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) =>
      api.complaint.subCategory.create(remoteUtilityId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Complaint sub-category created successfully",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintSubCategory.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintSubCategory.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useAllServiceConfig = (page: number) => {
  const limit = PAGE_SIZE;
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.list(),
    () => api.service.config.getAll(remoteUtilityId, page, limit),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useSearchServiceConfig = (page: number, search_data?: string) => {
  const limit = PAGE_SIZE;
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.search(search_data),
    () => api.service.config.search(remoteUtilityId, page, limit, search_data),
    {
      enabled: !!remoteUtilityId && !!search_data,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useServiceCategoryByUtility = (utility_service?: any[]) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.category.filter(utility_service),
    () => api.service.category.getByUtility(remoteUtilityId, utility_service),
    {
      enabled: !!utility_service && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useServiceSubCategoryByCategory = (code_list?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceSubCategory.list(code_list),
    () => api.service.subCategory.getByCategory(remoteUtilityId, code_list),
    {
      enabled: !!code_list && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const usePriorityLevel = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.priority.filter(),
    () => api.settings.getPriority(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useFieldTypes = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payment.getAllFieldTypes(),
    () => api.payment.mode.get.getAllFieldTypes(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useNotificationTemplate = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.template.filter(),
    () => api.settings.getNotificationTemplates(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useAvabilitySettings = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceConfig.availability.filter(),
    () => api.settings.getAvailability(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateServiceConfig = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (data: CreateServiceConfiguartion) => api.service.config.create(data),
    {
      onSuccess: () => {
        toast({
          title: "Service Configuration created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateServiceConfig = (service_id: number) => {
  const { toast } = useToast();
  return useSmartMutation(
    (data: updateServiceConfig) => api.service.config.update(data, service_id),
    {
      onSuccess: () => {
        toast({
          title: "Service category status updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.update(service_id),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useAllComplaintConfig = (page: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  const limit = PAGE_SIZE;
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.list(),
    () => api.complaint.config.getAll(remoteUtilityId, page, limit),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useToggleServiceConfig = () => {
  return useSmartMutation((data: any) => toggleServiceConfiguration(data), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Service status updated successfully.",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.serviceConfig.list(),
      });

      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.complaintConfig.list(),
      });
    },
  });
};

export const useSearchComplaintConfig = (
  page: number,
  search_data?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  const limit = PAGE_SIZE;
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.search(search_data),
    () =>
      api.complaint.config.search(remoteUtilityId, page, limit, search_data),
    {
      enabled: !!remoteUtilityId && !!search_data,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useComplaintCategoryByUtility = (utility_service?: any[]) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.category.filter(utility_service),
    () => api.complaint.category.getByUtility(remoteUtilityId, utility_service),
    {
      enabled: !!utility_service && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useComplaintSubCategoryByCategory = (code_list?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.subCategory.filter(code_list),
    () => api.complaint.subCategory.getByCategory(remoteUtilityId, code_list),
    {
      enabled: !!code_list && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useSeverityLevel = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.severity.filter(),
    () => api.settings.getSeverity(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useFrequency = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.complaintConfig.frequency.filter(),
    () => api.settings.getFrequency(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateComplaintConfig = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: CreateComplaintConfiguration) =>
      api.complaint.config.create(payload),
    {
      onSuccess: (newComplaintConfig) => {
        toast({
          title: "Complaint Configuration created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintConfig.create(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdatecomplaintConfig = (complaint_id: number) => {
  const { toast } = useToast();
  return useSmartMutation(
    (data: UpdateComplaintConfiguration) =>
      api.complaint.config.update(data, complaint_id),
    {
      onSuccess: (updatedServiceCategory) => {
        toast({
          title: "Success!",
          description: "Complaint category status updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.complaintConfig.update(complaint_id),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useDeleteServiceConfig = () => {
  return useSmartMutation(
    (service_id: number) => api.service.config.delete(service_id),
    {
      onSuccess: (updatedServiceCategory) => {
        toast({
          title: "Success!",
          description: "Service Configuration deleted successfully",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceConfig.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useDeleteComplaintConfig = () => {
  return useSmartMutation(
    (complaint_id: number) => api.complaint.config.delete(complaint_id),
    {
      onSuccess: (updatedServiceCategory) => {
        toast({
          title: "Success!",
          description: "Complaint Configuration deleted successfully",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.complaintConfig.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.complaintConfig.delete(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to delete Service Configuration",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetPaymentChannel = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payment.getChannel(),
    () => api.payment.channel.get.getChannel(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useUpdatePaymentChannelStatus = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.channel.update.updateChannelStatus(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment channel status updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getChannel(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.updateChannelStatus(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useCreatePaymentChannel = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.channel.create.createChannel(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment channel created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getChannel(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.createChannel(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdatePaymentChannel = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.channel.update.updateChannel(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment channel updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getChannel(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.updateChannel(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetPaymentMode = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payment.getMode(),
    () => api.payment.mode.get.getMode(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreatePaymentMode = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.mode.create.createMode(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment mode created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getMode(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.createMode(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdatePaymentMode = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.mode.update.updateMode(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment mode updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getMode(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.updateMode(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdatePaymentModeStatus = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.payment.mode.get.updateModeStatus(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment mode status updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getMode(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.updateModeStatus(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetDisconnectionReasons = (page: number, limit: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.get(),
    () =>
      api.operationalSLA.disconnectionReasons.get(remoteUtilityId, page, limit),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateReason = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.operationalSLA.disconnectionReasons.create(payload),
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Disconnection reason created successfully!",
        });
        const requestType = variables.request_type;
        switch (requestType) {
          case "Disconnect Permanent":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.get(),
            });
            break;
          case "Reconnection":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.reconnectionReasons.get(),
            });
            break;
          case "Transfer":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.transferTypes.get(),
            });
            break;
        }
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.create(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to create disconnection reason",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateReason = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.operationalSLA.disconnectionReasons.update(payload),
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Disconnection reason updated successfully!",
        });
        const requestType = variables.request_type;
        switch (requestType) {
          case "Disconnect Permanent":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.get(),
            });
            break;
          case "Reconnection":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.reconnectionReasons.get(),
            });
            break;
          case "Transfer":
            queryClient.refetchQueries({
              queryKey:
                QueryKeyFactory.module.cx.operationalSLA.transferTypes.get(),
            });
            break;
        }
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.update(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to update disconnection reason",
          variant: "destructive",
        });
      },
    }
  );
};

export const useDeleteReason = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.operationalSLA.disconnectionReasons.delete(payload),
    {
      onSuccess: () => {
        toast({
          title: "Disconnection reason deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.get(),
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.reconnectionReasons.get(),
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.transferTypes.get(),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.operationalSLA.disconnectionReasons.delete(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetReconnectionReasons = (page: number, limit: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.operationalSLA.reconnectionReasons.get(),
    () =>
      api.operationalSLA.reconnectionReasons.get(remoteUtilityId, page, limit),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetTransferTypes = (page: number, limit: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.operationalSLA.transferTypes.get(),
    () => api.operationalSLA.transferTypes.get(remoteUtilityId, page, limit),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const usePaymentFields = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payment.getAllPaymentFields(),
    () => api.payment.mode.get.getAllPaymentFields(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useDeletePaymentMode = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) =>
      api.payment.mode.delete.deleteMode(remoteUtilityId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment mode deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getMode(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.deleteMode(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useDeletePaymentChannel = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) =>
      api.payment.channel.delete.deleteChannel(remoteUtilityId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment channel deleted successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.payment.getChannel(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.payment.deleteChannel(),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useCreateNscResponseTimeConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.create(payload), {
    onSuccess: () => {
      toast({
        title: "NSC response time configuration created successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create NSC response time configuration",
        variant: "destructive",
      });
    },
  });
};

export const useGetNscResponseTimeConfiguration = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
    () => api.nsc.get(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useUpdateNscResponseTimeConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.update(payload), {
    onSuccess: () => {
      toast({
        title: "NSC response time configuration updated successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update NSC response time configuration",
        variant: "destructive",
      });
    },
  });
};

export const useGetAllAddressComponent = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nscSettings.getAllAddressComponent(),
    () => api.nsc.getAllAddressComponent(),
    {
      enabled: true,
    }
  );
};

export const useGetDocumentType = (configLevel: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const params = {
    remote_utility_id: remoteUtilityId,
    config_level: configLevel,
  };
  return useSmartQuery(
    QueryKeyFactory.module.cx.nscSettings.documentType(params),
    () => api.nsc.getDocumentType(params),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCreateNscDocumentTypeConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.createDocumentTypeConfiguration(payload), {
    onSuccess: () => {
      toast({
        title: "NSC document type configuration created successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create NSC document type configuration",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateNscDocumentTypeConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.updateDocumentTypeConfiguration(payload), {
    onSuccess: () => {
      toast({
        title: "NSC document type configuration updated successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update NSC document type configuration",
        variant: "destructive",
      });
    },
  });
};

export const useCreateNscAddressComponentConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.createAddressComponentConfiguration(payload), {
    onSuccess: () => {
      toast({
        title: "NSC address component configuration created successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create NSC address component configuration",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateNscAddressComponentConfiguration = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.nsc.updateAddressComponentConfiguration(payload), {
    onSuccess: () => {
      toast({
        title: "NSC address component configuration updated successfully!",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.cx.nscSettings.getResponseTimeConfiguration(),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update NSC address component configuration",
        variant: "destructive",
      });
    },
  });
};



